import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaUsers, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { playerAPI } from '../services/api';

const BRAND = { blue: '#043663', red: '#D52B1E', gray: '#E5E7EB', lightBg: '#f8fafc' };

const PAGE_SIZE = 10;

// ----- Layout -----
const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;
`;

const PageSubtitle = styled.p`
  font-size: 0.9375rem;
  color: #64748b;
  margin: 0 0 1.5rem 0;
  max-width: 560px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
`;

const Select = styled.select`
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid ${BRAND.gray};
  background: #fff;
  font-size: 0.9375rem;
  color: #0f172a;
  cursor: pointer;
  min-width: 160px;
`;

// ----- Summary cards -----
const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SummaryCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(4, 54, 99, 0.08);
  text-align: center;
`;

const SummaryValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${(p) => (p.$color ? p.$color : '#0f172a')};
`;
const SummaryLabel = styled.div`
  font-size: 0.8125rem;
  color: #64748b;
  margin-top: 0.25rem;
`;

// ----- Charts section -----
const ChartsSection = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 280px) 1fr minmax(0, 280px);
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(4, 54, 99, 0.06);
  border: 1px solid rgba(4, 54, 99, 0.08);
`;

const ChartCard = styled(Card)`
  min-height: 260px;
  display: flex;
  flex-direction: column;
`;

const ChartTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 1rem 0;
`;

const RatingPlaceholder = styled.div`
  height: 200px;
  background: linear-gradient(180deg, rgba(4, 54, 99, 0.04) 0%, rgba(4, 54, 99, 0.02) 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 0.9375rem;
  text-align: center;
  padding: 1rem;
`;

// ----- Recent History: full-width at bottom -----
const HistorySection = styled.section`
  width: 100%;
  margin-top: 1.5rem;
`;

const HistoryPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 200px;
`;

const HistoryTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 0.75rem 0;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

const Tab = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: none;
  background: ${(p) => (p.$active ? BRAND.blue : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : '#64748b')};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: ${(p) => (p.$active ? BRAND.blue : BRAND.lightBg)};
    color: ${(p) => (p.$active ? '#fff' : '#0f172a')};
  }
`;

// ----- Table -----
const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid ${BRAND.gray};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: #475569;
  background: ${BRAND.lightBg};
  border-bottom: 1px solid ${BRAND.gray};
  white-space: nowrap;
`;
const Td = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${BRAND.gray};
  color: #334155;
  vertical-align: middle;
  &:last-child {
    white-space: nowrap;
  }
`;
const Tr = styled.tr`
  &:hover {
    background: rgba(4, 54, 99, 0.02);
  }
  &:last-child ${Td} {
    border-bottom: none;
  }
`;

const ResultWin = styled.span`
  color: #059669;
  font-weight: 600;
`;
const ResultLoss = styled.span`
  color: #DC2626;
  font-weight: 600;
`;

const OpponentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${BRAND.blue};
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const EmptyTable = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9375rem;
`;

// ----- Pagination -----
const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const PageBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${BRAND.gray};
  background: #fff;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${BRAND.lightBg};
    border-color: ${BRAND.blue};
    color: ${BRAND.blue};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNum = styled.span`
  font-size: 0.875rem;
  color: #64748b;
`;

// ----- CTA & footer -----
const CtaLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  background: ${BRAND.blue};
  color: #fff;
  font-weight: 600;
  font-size: 0.9375rem;
  text-decoration: none;
  transition: background 0.2s, transform 0.15s;
  margin-top: 1rem;
  &:hover {
    background: #062a4d;
    color: #fff;
    transform: translateY(-1px);
  }
`;

const FooterNote = styled.p`
  font-size: 0.8125rem;
  color: #94a3b8;
  margin: 1.5rem 0 0;
  line-height: 1.5;
`;

function formatScore(m) {
  const parts = [];
  if (m.side1Set1Games != null && m.side2Set1Games != null) parts.push(`${m.side1Set1Games}-${m.side2Set1Games}`);
  if (m.side1Set2Games != null && m.side2Set2Games != null) parts.push(`${m.side1Set2Games}-${m.side2Set2Games}`);
  if (m.side1Set3Games != null && m.side2Set3Games != null) parts.push(`${m.side1Set3Games}-${m.side2Set3Games}`);
  if (parts.length) return parts.join(', ');
  if (m.side1Sets != null && m.side2Sets != null) return `${m.side1Sets}-${m.side2Sets} (sets)`;
  return '—';
}

function getInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name[0] || '?').toUpperCase();
}

export default function PlayerStatsPage() {
  const { t } = useTranslation();
  const [matchTypeFilter, setMatchTypeFilter] = useState('all');
  const [tablePage, setTablePage] = useState(0);

  const matchTypeOptions = useMemo(
    () => [
      { value: 'all', label: t('playerStatsPage.filterAll') },
      { value: 'ranked', label: t('playerStatsPage.filterRanked') },
      { value: 'friendly', label: t('playerStatsPage.filterFriendly') },
    ],
    [t]
  );

  const { data: statsRes, isLoading: statsLoading } = useQuery(
    ['playerStats'],
    () => playerAPI.getStats(),
    { staleTime: 30 * 1000 }
  );
  const { data: matchesRes, isLoading: matchesLoading } = useQuery(
    ['playerMatches', 'completed'],
    () => playerAPI.getMatches('completed'),
    { staleTime: 30 * 1000 }
  );

  const allMatches = useMemo(() => Array.isArray(matchesRes?.data) ? matchesRes.data : [], [matchesRes]);
  const filteredMatches = useMemo(() => {
    if (matchTypeFilter === 'all') return allMatches;
    return allMatches.filter((m) => (m.type || '').toLowerCase() === matchTypeFilter);
  }, [allMatches, matchTypeFilter]);

  const filteredStats = useMemo(() => {
    const total = filteredMatches.length;
    const wins = filteredMatches.filter((m) => m.resultLabel === 'Won').length;
    const losses = filteredMatches.filter((m) => m.resultLabel === 'Lost').length;
    const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;
    return { totalMatches: total, wins, losses, winRate };
  }, [filteredMatches]);

  const totalPages = Math.max(1, Math.ceil(filteredMatches.length / PAGE_SIZE));
  const pageIndex = Math.min(tablePage, totalPages - 1);
  const pageMatches = useMemo(
    () => filteredMatches.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE),
    [filteredMatches, pageIndex]
  );

  const isLoading = statsLoading || matchesLoading;

  return (
    <>
      <PageTitle>{t('playerStatsPage.title')}</PageTitle>
      <PageSubtitle>
        <Trans i18nKey="playerStatsPage.subtitle" components={{ strong: <strong /> }} />
      </PageSubtitle>

      <FilterRow>
        <FilterLabel>{t('playerStatsPage.filterLabel')}</FilterLabel>
        <Select
          value={matchTypeFilter}
          onChange={(e) => {
            setMatchTypeFilter(e.target.value);
            setTablePage(0);
          }}
        >
          {matchTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FilterRow>

      {isLoading && (
        <SummaryGrid>
          {[1, 2, 3, 4].map((i) => (
            <SummaryCard key={i}>
              <SummaryValue>—</SummaryValue>
              <SummaryLabel>{t('playerStatsPage.loadingCard')}</SummaryLabel>
            </SummaryCard>
          ))}
        </SummaryGrid>
      )}

      {!isLoading && (
        <>
          <SummaryGrid>
            <SummaryCard>
              <SummaryValue>{filteredStats.totalMatches}</SummaryValue>
              <SummaryLabel>{t('playerStatsPage.matchesPlayed')}</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue $color={BRAND.blue}>{filteredStats.wins}</SummaryValue>
              <SummaryLabel>{t('playerStatsPage.wins')}</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue $color={BRAND.red}>{filteredStats.losses}</SummaryValue>
              <SummaryLabel>{t('playerStatsPage.losses')}</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>{filteredStats.winRate}%</SummaryValue>
              <SummaryLabel>{t('playerStatsPage.winRate')}</SummaryLabel>
            </SummaryCard>
          </SummaryGrid>

          <ChartsSection>
            <ChartCard initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
              <ChartTitle>{t('playerStatsPage.chartWinsLosses')}</ChartTitle>
              {filteredStats.totalMatches > 0 ? (
                <PieChart
                  series={[
                    {
                      data: [
                        { id: 'wins', value: filteredStats.wins, label: t('playerStatsPage.pieLabelWins'), color: '#059669' },
                        { id: 'losses', value: filteredStats.losses, label: t('playerStatsPage.pieLabelLosses'), color: '#DC2626' },
                      ].filter((d) => d.value > 0),
                      highlightScope: { fade: 'global', highlight: 'item' },
                      innerRadius: 0,
                      outerRadius: 100,
                      paddingAngle: 2,
                    },
                  ]}
                  height={220}
                  slotProps={{ legend: { hidden: false } }}
                />
              ) : (
                <RatingPlaceholder>{t('playerStatsPage.placeholderWinLoss')}</RatingPlaceholder>
              )}
            </ChartCard>

            <ChartCard initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.05 }}>
              <ChartTitle>{t('playerStatsPage.chartRecentResults')}</ChartTitle>
              {filteredMatches.length > 0 ? (
                <BarChart
                  xAxis={[
                    {
                      scaleType: 'band',
                      data: filteredMatches
                        .slice(0, 10)
                        .reverse()
                        .map((m, i) => {
                          const d = m.completedAt || m.scheduledAt;
                          return d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : t('playerStatsPage.matchN', { n: i + 1 });
                        }),
                    },
                  ]}
                  series={[
                    {
                      data: filteredMatches
                        .slice(0, 10)
                        .reverse()
                        .map((m) => (m.resultLabel === 'Won' ? 1 : 0)),
                      label: t('playerStatsPage.barLabelWon'),
                      color: '#059669',
                      stack: 'result',
                    },
                    {
                      data: filteredMatches
                        .slice(0, 10)
                        .reverse()
                        .map((m) => (m.resultLabel === 'Lost' ? 1 : 0)),
                      label: t('playerStatsPage.barLabelLost'),
                      color: '#DC2626',
                      stack: 'result',
                    },
                  ]}
                  height={220}
                  margin={{ top: 20, bottom: 30, left: 30, right: 10 }}
                />
              ) : (
                <RatingPlaceholder>{t('playerStatsPage.placeholderRecent')}</RatingPlaceholder>
              )}
            </ChartCard>

            <ChartCard initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.1 }}>
              <ChartTitle>{t('playerStatsPage.chartRatingHistory')}</ChartTitle>
              <RatingPlaceholder>
                {t('playerStatsPage.placeholderRating')}
              </RatingPlaceholder>
            </ChartCard>
          </ChartsSection>

          <CtaLink to="/player/find-match">
            <FaUsers size={14} /> {t('playerStatsPage.ctaFindMatch')}
          </CtaLink>

          <FooterNote>
            {t('playerStatsPage.footerNote')}
          </FooterNote>

          <HistorySection>
            <Card initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.15 }}>
              <HistoryPanel>
                <HistoryTitle>{t('playerStatsPage.recentHistory')}</HistoryTitle>
                <Tabs>
                  {matchTypeOptions.map((opt) => (
                    <Tab
                      key={opt.value}
                      $active={matchTypeFilter === opt.value}
                      onClick={() => {
                        setMatchTypeFilter(opt.value);
                        setTablePage(0);
                      }}
                    >
                      {opt.label}
                    </Tab>
                  ))}
                </Tabs>

                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('playerStatsPage.colDate')}</Th>
                        <Th>{t('playerStatsPage.colOpponent')}</Th>
                        <Th>{t('playerStatsPage.colType')}</Th>
                        <Th>{t('playerStatsPage.colScore')}</Th>
                        <Th>{t('playerStatsPage.colResult')}</Th>
                        <Th>{t('playerStatsPage.colElo')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageMatches.length === 0 && (
                        <tr>
                          <td colSpan={6}>
                            <EmptyTable>
                              {filteredMatches.length === 0 && allMatches.length === 0
                                ? t('playerStatsPage.emptyNoMatches')
                                : matchTypeFilter === 'all'
                                  ? t('playerStatsPage.emptyFilteredAll')
                                  : t('playerStatsPage.emptyFiltered', { filter: matchTypeFilter })}
                            </EmptyTable>
                          </td>
                        </tr>
                      )}
                      {pageMatches.map((m) => (
                        <Tr key={m.id}>
                          <Td>
                            {m.completedAt
                              ? new Date(m.completedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                              : m.scheduledAt
                                ? new Date(m.scheduledAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                                : '—'}
                          </Td>
                          <Td>
                            <OpponentCell>
                              <Avatar>{getInitials(m.opponent?.name)}</Avatar>
                              {m.opponent?.name || t('playerStatsPage.opponentFallback')}
                            </OpponentCell>
                          </Td>
                          <Td>{(m.type || 'friendly').charAt(0).toUpperCase() + (m.type || 'friendly').slice(1)}</Td>
                          <Td>{formatScore(m)}</Td>
                          <Td>
                            {m.resultLabel === 'Won' && <ResultWin>{t('playerStatsPage.resultWin')}</ResultWin>}
                            {m.resultLabel === 'Lost' && <ResultLoss>{t('playerStatsPage.resultLoss')}</ResultLoss>}
                            {!m.resultLabel && '—'}
                          </Td>
                          <Td style={{ color: '#94a3b8' }}>—</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>

                {filteredMatches.length > PAGE_SIZE && (
                  <PaginationRow>
                    <PageBtn
                      type="button"
                      disabled={pageIndex === 0}
                      onClick={() => setTablePage((p) => Math.max(0, p - 1))}
                      aria-label={t('playerStatsPage.ariaPrev')}
                    >
                      <FaChevronLeft />
                    </PageBtn>
                    <PageNum>
                      {totalPages > 1
                        ? t('playerStatsPage.pageOf', { current: pageIndex + 1, total: totalPages })
                        : pageIndex + 1}
                    </PageNum>
                    <PageBtn
                      type="button"
                      disabled={pageIndex >= totalPages - 1}
                      onClick={() => setTablePage((p) => Math.min(totalPages - 1, p + 1))}
                      aria-label={t('playerStatsPage.ariaNext')}
                    >
                      <FaChevronRight />
                    </PageBtn>
                  </PaginationRow>
                )}
              </HistoryPanel>
            </Card>
          </HistorySection>
        </>
      )}
    </>
  );
}
